import React, { useState } from "react";
import { PageConsole } from "@/components/PageConsole";
import { ChevronDown } from "@/components/icons";

// The type plate. Every machine on site carries one: brand, type, serial, year.
// Same six positions every time so the eye learns a position, not a label.
// Flat colour, fixed grid, mono type — it reads as a plate without imitating one.

// No value, no field: not even an empty slot. The plate is a grid, so a missing
// field simply closes up.
const PlateField = ({ label, value, mono }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="min-w-0">
      <div className="text-label font-semibold uppercase tracking-[0.07em] text-plaat-mist">{label}</div>
      <div className={`truncate text-data text-white ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
    </div>
  );
};

const year = (iso) => (iso ? iso.slice(0, 4) : null);

// Purchase -> today -> replacement -> technical limit, on one line.
// AssetDetail only: never in a list, where a column of dates reads faster.
const LifetimeBar = ({ purchaseDate, replacementDate, limitDate }) => {
  const start = purchaseDate ? new Date(purchaseDate).getTime() : null;
  const end = new Date(limitDate || replacementDate || "").getTime();
  if (!start || !end || end <= start) return null;
  const span = end - start;
  const clamp = (v) => Math.min(100, Math.max(0, v));
  const pos = (d) => clamp(((new Date(d).getTime() - start) / span) * 100);
  const todayPct = pos(new Date().toISOString().slice(0, 10));
  const replPct = replacementDate ? pos(replacementDate) : null;
  const over = replacementDate && new Date(replacementDate) < new Date();
  return (
    <div className="mt-4 border-t border-plaat-lijn pt-3" data-testid="plaatkop-lifetime">
      <div className="relative h-1.5 w-full rounded-sm bg-plaat-lijn">
        <div
          className={`absolute inset-y-0 left-0 rounded-sm ${over ? "bg-fout-rand" : "bg-plaat-mist"}`}
          style={{ width: `${todayPct}%` }}
        />
        {replPct !== null && (
          <div className="absolute inset-y-[-3px] w-[2px] bg-white" style={{ left: `${replPct}%` }} />
        )}
      </div>
      <div className="mt-1.5 flex justify-between font-mono text-label tabular-nums text-plaat-mist">
        <span>{year(purchaseDate)}</span>
        <span className={over ? "text-fout-rand" : "text-plaat-mist"}>
          {replacementDate ? `vervangen ${replacementDate.split("-").reverse().join("-")}` : ""}
        </span>
        <span>{year(limitDate || replacementDate)}</span>
      </div>
    </div>
  );
};

export const Plaatkop = ({
  code,
  srTitle,
  srTestid,
  subtitle,
  fields = [],
  actions,
  lifetime,
  collapsibleFields = false,
  backSlot,
  testid = "plaatkop",
}) => {
  const [openFields, setOpenFields] = useState(false);
  // Steel band = you are looking at an object with a plate bolted to it.
  //
  // The band is sticky, so everything in it is pinned for the whole page. The
  // full plate is 342px on a phone — 51% of the scroll area — which hid the tab
  // strip under it. The plate therefore shows the grid and the lifetime bar only
  // while the page is at rest; the moment it sticks, it keeps the code, the
  // subtitle and the actions and drops the rest.
  return (
    <PageConsole variant="object" code={code} srTitle={srTitle} srTestid={srTestid} backSlot={backSlot} testid={testid}>
      {(collapsed) => (
        <>
          {actions && <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div>}
          {subtitle && <div className="mt-2 text-data text-plaat-mist">{subtitle}</div>}

          {fields.length > 0 && !collapsed && (
            <>
              {collapsibleFields && (
                <button
                  type="button"
                  data-testid={`${testid}-fields-toggle`}
                  onClick={() => setOpenFields((v) => !v)}
                  className="mt-3 inline-flex items-center gap-1 text-label font-semibold uppercase tracking-[0.07em] text-plaat-mist sm:hidden"
                >
                  Gegevens <ChevronDown size={14} className={openFields ? "rotate-180" : ""} />
                </button>
              )}
              <div
                className={`mt-3 grid-cols-2 gap-x-6 gap-y-3 border-t border-plaat-lijn pt-3 sm:grid sm:grid-cols-3 lg:grid-cols-6 ${
                  collapsibleFields && !openFields ? "hidden" : "grid"
                }`}
              >
                {fields.map((f) => (
                  <PlateField key={f.label} {...f} />
                ))}
              </div>
            </>
          )}

          {lifetime && !collapsed && <LifetimeBar {...lifetime} />}
        </>
      )}
    </PageConsole>
  );
};
